import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ScenarioMiniCard from './ScenarioMiniCard';
import './TestGenerationModal.css';
import apiClient from '../utils/apiClient';

const TestGenerationModal = ({ 
  isOpen, 
  onClose,
  selectedNode,
  generateTests,
  fetchNodes,
  addPendingNode
}) => {
  const { t, i18n } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedTests, setGeneratedTests] = useState(null);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [scenariosStatus, setScenariosStatus] = useState({}); // Pour gérer le statut de chaque scénario
  const [parsedScenarios, setParsedScenarios] = useState([]); // Cache des scénarios parsés pour éviter les re-parsing

  // Fonction pour parser les scénarios depuis le texte généré (adaptée de PendingNodesPanel)
  const parseDescriptionForScenarios = (description) => {
    if (!description) return [];

    const scenarios = [];
    let scenarioCounter = 0; // Pour éviter les doublons de clés

    try {
      // === 1. PARSER LE JSON DANS LES BLOCS DE CODE ===
      const jsonBlockRegex = /```(?:json)?\s*([\s\S]*?)\s*```/gi;
      let jsonMatch;

      while ((jsonMatch = jsonBlockRegex.exec(description)) !== null) {
        try {
          const jsonContent = jsonMatch[1].trim();
          const parsedJson = JSON.parse(jsonContent);

          // Si c'est un tableau de scénarios
          if (Array.isArray(parsedJson)) {
            parsedJson.forEach((item, index) => {
              if (item && typeof item === 'object') {
                scenarios.push({
                  id: item.id || `json-scenario-${Date.now()}-${scenarioCounter++}-${Math.random().toString(36).substr(2, 9)}`,
                  uniqueKey: `json-scenario-${Date.now()}-${scenarioCounter}-${Math.random().toString(36).substr(2, 9)}`,
                  title: item.title || item.name || `Scénario JSON ${index + 1}`,
                  type: item.type || 'acceptance',
                  positive: item.positive !== false,
                  steps: item.steps || [],
                  description: item.description || '',
                  status: 'pending'
                });
              }
            });
          }
          // Si c'est un objet unique avec des scénarios
          else if (parsedJson && typeof parsedJson === 'object') {
            if (parsedJson.scenarios && Array.isArray(parsedJson.scenarios)) {
              parsedJson.scenarios.forEach((scenario, index) => {
                scenarios.push({
                  id: scenario.id || `json-obj-scenario-${Date.now()}-${scenarioCounter++}-${Math.random().toString(36).substr(2, 9)}`,
                  uniqueKey: `json-obj-scenario-${Date.now()}-${scenarioCounter}-${Math.random().toString(36).substr(2, 9)}`,
                  title: scenario.title || scenario.name || `Scénario ${index + 1}`,
                  type: scenario.type || 'acceptance',
                  positive: scenario.positive !== false,
                  steps: scenario.steps || [],
                  description: scenario.description || '',
                  status: 'pending'
                });
              });
            } else if (parsedJson.steps) {
              // Un seul scénario
              scenarios.push({
                id: parsedJson.id || `single-json-scenario-${Date.now()}-${scenarioCounter++}-${Math.random().toString(36).substr(2, 9)}`,
                uniqueKey: `single-json-scenario-${Date.now()}-${scenarioCounter}-${Math.random().toString(36).substr(2, 9)}`,
                title: parsedJson.title || parsedJson.name || 'Scénario JSON',
                type: parsedJson.type || 'acceptance',
                positive: parsedJson.positive !== false,
                steps: parsedJson.steps,
                description: parsedJson.description || '',
                status: 'pending'
              });
            }
          }
        } catch (jsonError) {
          console.warn('Erreur de parsing JSON:', jsonError);
        }
      }

      // === 2. PARSER LES SCÉNARIOS GHERKIN ===
      const gherkinRegex = /(?:^|\n)\s*(?:Scenario|Scénario)(?:\s+Outline)?:\s*([^\n]+)((?:\n\s*(?:Given|When|Then|And|But|Soit|Quand|Alors|Et|Mais|Etant donné que|Lorsque|Alors que)\s+[^\n]+)*)/gmi;
      let gherkinMatch;

      while ((gherkinMatch = gherkinRegex.exec(description)) !== null) {
        const title = gherkinMatch[1].trim();
        const stepsText = gherkinMatch[2];
        
        const steps = [];
        const stepRegex = /(?:^|\n)\s*(Given|When|Then|And|But|Soit|Quand|Alors|Et|Mais|Etant donné que|Lorsque|Alors que)\s+([^\n]+)/gmi;
        let stepMatch;

        while ((stepMatch = stepRegex.exec(stepsText)) !== null) {
          steps.push({
            type: stepMatch[1].trim(),
            text: stepMatch[2].trim()
          });
        }

        if (steps.length > 0) {
          scenarios.push({
            id: `gherkin-scenario-${Date.now()}-${scenarioCounter++}-${Math.random().toString(36).substr(2, 9)}`,
            uniqueKey: `gherkin-scenario-${Date.now()}-${scenarioCounter}-${Math.random().toString(36).substr(2, 9)}`,
            title: title,
            type: 'acceptance',
            positive: !title.toLowerCase().includes('error') && !title.toLowerCase().includes('échec'),
            steps: steps,
            status: 'pending'
          });
        }
      }

      // === 3. FALLBACK: PARSER PAR SECTIONS ===
      if (scenarios.length === 0) {
        const sections = description.split(/\n\s*\n/).filter(section => section.trim());
        sections.forEach((section, index) => {
          const lines = section.split('\n').filter(line => line.trim());
          if (lines.length > 1) {
            const title = lines[0].replace(/^[#*\-\d\.]+\s*/, '').trim();
            const steps = lines.slice(1).map(line => ({
              type: 'Step',
              text: line.replace(/^[\-\*\d\.]+\s*/, '').trim()
            }));

            scenarios.push({
              id: `section-scenario-${Date.now()}-${scenarioCounter++}-${Math.random().toString(36).substr(2, 9)}`,
              uniqueKey: `section-scenario-${Date.now()}-${scenarioCounter}-${Math.random().toString(36).substr(2, 9)}`,
              title: title || `Section ${index + 1}`,
              type: 'acceptance',
              positive: true,
              steps: steps,
              status: 'pending'
            });
          }
        });
      }

    } catch (error) {
      console.warn('Erreur lors du parsing des scénarios:', error);
    }

    // Si aucun scénario trouvé, créer un scénario par défaut
    if (scenarios.length === 0 && description.trim()) {
      scenarios.push({
        id: `default-scenario-${Date.now()}-${scenarioCounter++}-${Math.random().toString(36).substr(2, 9)}`,
        uniqueKey: `default-scenario-${Date.now()}-${scenarioCounter}-${Math.random().toString(36).substr(2, 9)}`,
        title: t('testGenerationModal.parsing.generatedContent'),
        type: 'acceptance',
        positive: true,
        steps: [{ type: 'Content', text: description.trim() }],
        status: 'pending'
      });
    }

    return scenarios;
  };

  // Effect pour parser les scénarios une seule fois quand les tests sont générés
  useEffect(() => {
    if (generatedTests) {
      console.log('🔄 Parsing scenarios from generatedTests...');
      const scenarios = parseDescriptionForScenarios(
        typeof generatedTests === 'string' ? generatedTests : JSON.stringify(generatedTests, null, 2)
      );
      console.log('✅ Parsed scenarios cached:', scenarios);
      setParsedScenarios(scenarios);
    } else {
      setParsedScenarios([]);
    }
  }, [generatedTests]);

  // Fonction pour changer le statut d'un scénario
  const handleScenarioAction = (scenarioId, action) => {
    console.log('🎯 handleScenarioAction called:', { scenarioId, action });
    console.log('🎯 Current scenariosStatus:', scenariosStatus);
    
    setScenariosStatus(prev => {
      const newStatus = {
        ...prev,
        [scenarioId]: action
      };
      console.log('🎯 New scenariosStatus:', newStatus);
      return newStatus;
    });
  };

  // Fonction pour sauvegarder les scénarios approuvés
  const handleSaveApprovedScenarios = async (saveMode = 'direct') => {
    if (!selectedNode || !generateTests || !fetchNodes) return;

    // Utiliser les scénarios pré-parsés
    const scenarios = parsedScenarios;
    const approvedScenarios = scenarios.filter(s => scenariosStatus[s.id] === 'approved');
    
    console.log('💾 Saving scenarios:', { total: scenarios.length, approved: approvedScenarios.length, mode: saveMode });
    
    if (approvedScenarios.length === 0) {
      alert(t('testGenerationModal.alerts.noApprovedScenarios'));
      return;
    }

    try {
      if (saveMode === 'direct') {
        // MODE DIRECT : Utiliser l'API /add-scenarios pour ajouter directement au nœud
        console.log(`🎯 Ajout DIRECT de ${approvedScenarios.length} tests au nœud "${selectedNode.name}"...`);
        
        const testsToAdd = approvedScenarios.map(scenario => ({
          id: scenario.id,
          title: scenario.title,
          description: `Test généré automatiquement`,
          type: scenario.type || 'acceptance',
          steps: scenario.steps || [],
          positive: scenario.positive !== false,
          category: 'generated',
          priority: 'medium',
          author: 'System',
          createdAt: new Date().toISOString(),
          metadata: {
            scenarioType: scenario.type || 'acceptance',
            generatedFor: selectedNode.name,
            originalScenarioId: scenario.id,
            directlyAdded: true
          }
        }));

        const response = await apiClient.post('/add-scenarios', {
          scenarios: testsToAdd,
          relatedNodeName: selectedNode.name
        });

        if (response.data.success) {
          const result = response.data;
          console.log(`✅ ${testsToAdd.length} tests ajoutés DIRECTEMENT au nœud "${selectedNode.name}":`, result);
          alert(t('testGenerationModal.alerts.directSuccess', { 
            count: testsToAdd.length, 
            nodeName: selectedNode.name || selectedNode.nom 
          }));
        } else {
          throw new Error(`Erreur API: ${response.data.error || 'Erreur inconnue'}`);
        }

      } else {
        // MODE MODÉLISATION : Ajouter aux éléments en modélisation
        console.log(`📋 Ajout de ${approvedScenarios.length} tests aux éléments en modélisation...`);
        
        for (const scenario of approvedScenarios) {
          const testData = {
            name: scenario.title,
            title: scenario.title,
            type: "Test",
            description: `Test automatique généré pour ${selectedNode.name}`,
            targetNode: selectedNode.name,
            nodeName: selectedNode.name,
            sourceNode: selectedNode.name,
            metadata: {
              scenarioType: scenario.type || 'acceptance',
              steps: scenario.steps || [],
              positive: scenario.positive !== false,
              category: 'generated',
              priority: 'medium',
              isTest: true,
              sourceNode: selectedNode.name,
              targetNode: selectedNode.name,
              originalScenarioId: scenario.id,
              saveMode: 'modeling'
            },
            steps: scenario.steps || [],
            positive: scenario.positive !== false,
            category: scenario.category || 'functional',
            priority: scenario.priority || 'medium',
            author: 'System'
          };

          if (addPendingNode) {
            addPendingNode(testData);
            console.log(`✅ Test "${scenario.title}" ajouté aux éléments en modélisation`);
          }
        }
        
        alert(t('testGenerationModal.alerts.modelingSuccess', { count: approvedScenarios.length }));
      }

      // Rafraîchir les données pour voir les changements
      if (fetchNodes) {
        await fetchNodes();
      }

      // Fermer la modal après sauvegarde
      handleClose();

    } catch (error) {
      console.error('Erreur lors de la sauvegarde des tests approuvés:', error);
      alert(t('testGenerationModal.alerts.saveError', { error: error.message }));
    }
  };

  const handleClose = () => {
    // Nettoyer l'état quand on ferme
    setIsGenerating(false);
    setGeneratedTests(null);
    setError(null);
    setCurrentStep(0);
    setScenariosStatus({});
    setParsedScenarios([]);
    onClose();
  };

  const handleGenerate = async () => {
    if (!selectedNode || !generateTests) return;
    
    setIsGenerating(true);
    setError(null);
    setGeneratedTests(null);
    setCurrentStep(0);
    setScenariosStatus({}); // Reset scenario status
    setParsedScenarios([]); // Reset parsed scenarios
    
    try {
      // Étape 1 : Analyse du nœud
      setCurrentStep(1);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Étape 2 : Identification des critères
      setCurrentStep(2);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Étape 3 : Génération des tests
      setCurrentStep(3);
      
      // Appeler la fonction de génération existante
      console.log("🔄 Appel de generateTests()...");
      const result = await generateTests();
      console.log("✅ Résultat de generateTests:", result);
      
      // Étape 4 : Finalisation
      setCurrentStep(4);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      console.log("📋 Setting generatedTests:", result);
      setGeneratedTests(result);
      
      // Rafraîchir les données des nœuds après génération
      if (fetchNodes) {
        await fetchNodes();
      }
    } catch (err) {
      console.error("Erreur lors de la génération:", err);
      setError(err.message || "Erreur lors de la génération des tests");
    } finally {
      setIsGenerating(false);
      setCurrentStep(0);
    }
  };

  if (!isOpen) return null;

  // Debug logs pour voir les états
  console.log("🎭 Modal states:", { 
    isGenerating, 
    hasGeneratedTests: !!generatedTests, 
    hasError: !!error,
    currentStep
  });

  return (
    <div className="modal-overlay test-generation-overlay" onClick={handleClose}>
      <div className="modal-content test-generation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
                    <h2 className="generation-header">
            {t('testGenerationModal.title', { nodeName: selectedNode.nom })}
          </h2>
          <button className="modal-close-btn" onClick={handleClose}>×</button>
        </div>

        <div className="modal-body test-generation-body">
          {!isGenerating && !generatedTests && !error && (
            <div className="generation-start">
              <div className="node-info">
                <div className="node-header">
                  <span className="node-icon">📄</span>
                  <div>
                    <h3>{selectedNode?.name}</h3>
                    <p>{selectedNode?.description}</p>
                  </div>
                </div>
                
                <div className="node-details">
                  <div className="detail-item">
                    <span className="detail-label">{t('testGenerationModal.nodeDetails.type')}</span>
                    <span className="detail-value">{selectedNode?.type}</span>
                  </div>
                  {selectedNode?.technology && (
                    <div className="detail-item">
                      <span className="detail-label">{t('testGenerationModal.nodeDetails.technology')}</span>
                      <span className="detail-value">{selectedNode?.technology}</span>
                    </div>
                  )}
                  {selectedNode?.author && (
                    <div className="detail-item">
                      <span className="detail-label">{t('testGenerationModal.nodeDetails.author')}</span>
                      <span className="detail-value">{selectedNode?.author}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="generation-info">
                <h4>{t('testGenerationModal.generationInfo.title')}</h4>
                <ul>
                  <li>{t('testGenerationModal.generationInfo.acceptanceCriteria')}</li>
                  <li>{t('testGenerationModal.generationInfo.manualScenarios')}</li>
                  <li>{t('testGenerationModal.generationInfo.exploratoryTests')}</li>
                  <li>{t('testGenerationModal.generationInfo.edgeCases')}</li>
                  <li>{t('testGenerationModal.generationInfo.testData')}</li>
                </ul>
              </div>
              
              <button 
                className="generate-btn primary"
                onClick={handleGenerate}
              >
                {t('testGenerationModal.generateButton')}
              </button>
            </div>
          )}

          {isGenerating && (
            <div className="generation-progress">
              <div className="spinner-container">
                <div className="generation-spinner"></div>
                <h3>{t('testGenerationModal.generating.title')}</h3>
                <p>{t('testGenerationModal.generating.subtitle')}</p>
              </div>
              
              <div className="generation-steps">
                <div className={`step ${currentStep >= 1 ? 'completed' : ''}`}>
                  {currentStep >= 1 ? '✅' : '⏳'} {t('testGenerationModal.generating.steps.nodeAnalysis')}
                </div>
                <div className={`step ${currentStep >= 2 ? 'completed' : currentStep === 1 ? 'active' : ''}`}>
                  {currentStep >= 2 ? '✅' : currentStep === 1 ? '⚡' : '⏳'} {t('testGenerationModal.generating.steps.criteriaIdentification')}
                </div>
                <div className={`step ${currentStep >= 3 ? 'completed' : currentStep === 2 ? 'active' : ''}`}>
                  {currentStep >= 3 ? '✅' : currentStep === 2 ? '🧪' : '⏳'} {t('testGenerationModal.generating.steps.testGeneration')}
                </div>
                <div className={`step ${currentStep >= 4 ? 'completed' : currentStep === 3 ? 'active' : ''}`}>
                  {currentStep >= 4 ? '✅' : currentStep === 3 ? '📝' : '⏳'} {t('testGenerationModal.generating.steps.finalization')}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="generation-error">
              <div className="error-icon">❌</div>
              <h3>{t('testGenerationModal.error.title')}</h3>
              <p>{error}</p>
              <div className="error-actions">
                <button className="retry-btn" onClick={handleGenerate}>
                  {t('testGenerationModal.error.retryButton')}
                </button>
                <button className="close-error-btn" onClick={handleClose}>
                  {t('testGenerationModal.error.closeButton')}
                </button>
              </div>
            </div>
          )}

          {generatedTests && (
            <div className="generation-results">
              <div className="success-header">
                <div className="success-icon">🎉</div>
                <h3>{t('testGenerationModal.results.title')}</h3>
                <p>{t('testGenerationModal.results.subtitle')}</p>
              </div>
              
              <div className="scenarios-validation-section">
                {(() => {
                  // Utiliser les scénarios pré-parsés au lieu de re-parser
                  const scenarios = parsedScenarios;
                  console.log('🔍 Using cached scenarios:', scenarios);
                  console.log('🔍 Current scenariosStatus:', scenariosStatus);
                  
                  const approved = scenarios.filter(s => scenariosStatus[s.id] === 'approved').length;
                  const rejected = scenarios.filter(s => scenariosStatus[s.id] === 'rejected').length;
                  const pending = scenarios.length - approved - rejected;

                  console.log('🔍 Stats:', { approved, rejected, pending, totalScenarios: scenarios.length });

                  return (
                    <>
                      <div className="validation-stats-header">
                        <h4>{t('testGenerationModal.results.scenariosCount', { count: scenarios.length })}</h4>
                        <div className="stats-badges">
                          <span className="stat-badge approved">{t('testGenerationModal.results.approved', { count: approved })}</span>
                          <span className="stat-badge rejected">{t('testGenerationModal.results.rejected', { count: rejected })}</span>
                          <span className="stat-badge pending">{t('testGenerationModal.results.pending', { count: pending })}</span>
                        </div>
                      </div>

                      <div className="scenarios-container">
                        {scenarios.map((scenario, index) => {
                          const status = scenariosStatus[scenario.id] || 'pending';
                          console.log(`🔍 Scenario ${index + 1}:`, { id: scenario.id, title: scenario.title, status });
                          
                          return (
                            <div key={scenario.id || scenario.uniqueKey || `validation-scenario-${index}-${Date.now()}`} className={`scenario-validation-item ${status}`}>
                              <div className="scenario-item-header">
                                <div className="scenario-info">
                                  <span className="scenario-number">{t('testGenerationModal.results.scenarioNumber', { number: index + 1 })}</span>
                                  <h5 className="scenario-title-text">{scenario.title}</h5>
                                </div>
                                
                                <div className="validation-buttons">
                                  <button 
                                    className={`validation-btn approve-btn ${status === 'approved' ? 'active' : ''}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      console.log('🔘 Approve button clicked for scenario:', scenario.id);
                                      handleScenarioAction(scenario.id, 'approved');
                                    }}
                                    title={t('testGenerationModal.results.approveTooltip')}
                                    type="button"
                                  >
                                    ✅
                                  </button>
                                  <button 
                                    className={`validation-btn pending-btn ${status === 'pending' ? 'active' : ''}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      console.log('🔘 Pending button clicked for scenario:', scenario.id);
                                      handleScenarioAction(scenario.id, 'pending');
                                    }}
                                    title={t('testGenerationModal.results.pendingTooltip')}
                                    type="button"
                                  >
                                    ⏳
                                  </button>
                                  <button 
                                    className={`validation-btn reject-btn ${status === 'rejected' ? 'active' : ''}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      console.log('🔘 Reject button clicked for scenario:', scenario.id);
                                      handleScenarioAction(scenario.id, 'rejected');
                                    }}
                                    title={t('testGenerationModal.results.rejectTooltip')}
                                    type="button"
                                  >
                                    ❌
                                  </button>
                                </div>
                              </div>
                              
                              <div className="scenario-content-wrapper">
                                <ScenarioMiniCard scenario={scenario} />
                              </div>
                              
                              {status !== 'pending' && (
                                <div className={`status-indicator ${status}`}>
                                  {status === 'approved' 
                                    ? t('testGenerationModal.results.approvedStatus') 
                                    : t('testGenerationModal.results.rejectedStatus')
                                  }
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      <div className="bulk-actions">
                        <button 
                          className="bulk-btn approve-all-btn"
                          onClick={() => {
                            console.log('🔘 Approve all clicked, scenarios:', scenarios);
                            const newStatus = {};
                            scenarios.forEach(s => {
                              console.log('🔘 Setting status for scenario:', s.id);
                              newStatus[s.id] = 'approved';
                            });
                            console.log('🔘 Bulk approve newStatus:', newStatus);
                            setScenariosStatus(newStatus);
                          }}
                        >
                          {t('testGenerationModal.results.bulkActions.approveAll')}
                        </button>
                        <button 
                          className="bulk-btn reject-all-btn"
                          onClick={() => {
                            console.log('🔘 Reject all clicked');
                            const newStatus = {};
                            scenarios.forEach(s => newStatus[s.id] = 'rejected');
                            setScenariosStatus(newStatus);
                          }}
                        >
                          {t('testGenerationModal.results.bulkActions.rejectAll')}
                        </button>
                        <button 
                          className="bulk-btn reset-btn"
                          onClick={() => {
                            console.log('🔘 Reset clicked');
                            setScenariosStatus({});
                          }}
                        >
                          {t('testGenerationModal.results.bulkActions.reset')}
                        </button>
                      </div>

                      <div className="save-actions">
                        <div className="save-options">
                          <h4>{t('testGenerationModal.results.saveActions.title')}</h4>
                          <div className="save-buttons">
                            <button 
                              className="save-btn primary direct-save"
                              onClick={() => handleSaveApprovedScenarios('direct')}
                              disabled={approved === 0}
                              title={t('testGenerationModal.results.saveActions.directTooltip')}
                            >
                              {t('testGenerationModal.results.saveActions.directButton', { 
                                nodeName: selectedNode?.name || selectedNode?.nom || 'Node', 
                                count: approved 
                              })}
                            </button>
                            
                            <button 
                              className="save-btn secondary modeling-save"
                              onClick={() => handleSaveApprovedScenarios('modeling')}
                              disabled={approved === 0}
                              title={t('testGenerationModal.results.saveActions.modelingTooltip')}
                            >
                              {t('testGenerationModal.results.saveActions.modelingButton', { count: approved })}
                            </button>
                          </div>
                          
                          <div className="save-explanation">
                            <div className="save-option-info direct">
                              <span className="info-icon">🎯</span>
                              <span className="info-text">
                                {t('testGenerationModal.results.saveActions.directInfo')}
                              </span>
                            </div>
                            <div className="save-option-info modeling">
                              <span className="info-icon">📋</span>
                              <span className="info-text">{t('testGenerationModal.results.saveActions.modelingInfo')}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>

              <details className="raw-content-details">
                <summary className="raw-content-summary">
                  {t('testGenerationModal.results.rawContent')}
                </summary>
                <div className="raw-content-display">
                  <pre>{typeof generatedTests === 'string' 
                    ? generatedTests
                    : JSON.stringify(generatedTests, null, 2)
                  }</pre>
                </div>
              </details>

              <div className="results-actions">
                <button className="close-btn primary" onClick={handleClose}>
                  {t('testGenerationModal.results.closeButton')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestGenerationModal;